import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Home, FileText, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, failed, pending
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(5);

  // Get subdomain from URL params or localStorage
  const subdomain = searchParams.get('subdomain') || localStorage.getItem('clientSubdomain');
  const paymentType = searchParams.get('type') || 'purchase';

  useEffect(() => {
    const verifyPayment = async () => {
      // Get params from URL (Razorpay callback)
      const razorpayPaymentId = searchParams.get('razorpay_payment_id');
      const razorpayPaymentLinkStatus = searchParams.get('razorpay_payment_link_status');

      // Get stored transaction info
      const pendingTransaction = localStorage.getItem('pendingPaymentTransaction');
      let transactionData = null;
      
      if (pendingTransaction) {
        try {
          transactionData = JSON.parse(pendingTransaction);
        } catch (e) {
          console.error('Failed to parse pending transaction:', e);
        }
      }

      // If payment link status is paid, show success
      if (razorpayPaymentLinkStatus === 'paid') {
        setStatus('success');
        setPaymentInfo({
          payment_id: razorpayPaymentId,
          payment_type: paymentType === 'renewal' ? 'will_renewal' : 'will_creation',
          will_id: transactionData?.will_id
        });
        
        // Clear stored transaction
        localStorage.removeItem('pendingPaymentTransaction');
        
        // Show toast
        if (paymentType === 'renewal') {
          toast.success('Payment successful! Your will access has been renewed.', { duration: 5000 });
        } else {
          toast.success('Payment successful! Your will has been created.', { duration: 5000 });
        }
        
        return;
      }

      // If no status in URL but we have transaction data, check status
      if (transactionData?.transaction_id) {
        try {
          const token = localStorage.getItem('clientToken') || localStorage.getItem('partnerToken');
          const response = await axios.get(
            `${API}/payment/status/${transactionData.transaction_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.status === 'paid') {
            setStatus('success');
            setPaymentInfo(response.data);
            localStorage.removeItem('pendingPaymentTransaction');
            toast.success('Payment successful!', { duration: 5000 });
          } else if (response.data.status === 'initiated') {
            setStatus('pending');
            setPaymentInfo(response.data);
          } else {
            setStatus('failed');
            setError('Payment was not completed');
          }
        } catch (err) {
          console.error('Failed to check payment status:', err);
          setStatus('pending');
          setError('Unable to verify payment status. Please check your dashboard.');
        }
        return;
      }

      // No payment info available but URL says paid
      if (razorpayPaymentLinkStatus === 'paid') {
        setStatus('success');
        setPaymentInfo({ payment_type: paymentType === 'renewal' ? 'will_renewal' : 'will_creation' });
        toast.success('Payment successful!', { duration: 5000 });
        return;
      }

      // Fallback - show pending
      setStatus('pending');
    };

    verifyPayment();
  }, [searchParams, paymentType]);

  // Auto-redirect countdown for success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      handleGoToDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, countdown]);

  const handleGoToDashboard = () => {
    const clientToken = localStorage.getItem('clientToken');
    const partnerToken = localStorage.getItem('partnerToken');
    const storedSubdomain = subdomain || localStorage.getItem('clientSubdomain');
    
    if (clientToken && storedSubdomain) {
      navigate('/will');
    } else if (partnerToken) {
      navigate('/partner/dashboard');
    } else if (storedSubdomain) {
      navigate('/');
    } else {
      navigate('/');
    }
  };

  const handleGoToWill = () => {
    const storedSubdomain = subdomain || localStorage.getItem('clientSubdomain');
    if (paymentInfo?.will_id && storedSubdomain) {
      navigate(`/will/create/${paymentInfo.will_id}`);
    } else {
      handleGoToDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-900 rounded-2xl border border-slate-800 p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Verifying Payment</h1>
            <p className="text-slate-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-slate-400 mb-4">
              {paymentInfo?.payment_type === 'will_renewal'
                ? 'Your will access has been extended successfully.'
                : paymentInfo?.payment_type === 'partner_onboarding'
                ? 'Your onboarding payment is complete. You can now add clients.'
                : 'Your will has been created. Start editing now!'}
            </p>
            
            {/* Auto-redirect countdown */}
            <p className="text-amber-400 text-sm mb-6">
              Redirecting to dashboard in {countdown} seconds...
            </p>
            
            {paymentInfo?.payment_id && (
              <div className="mb-6 p-4 bg-slate-800/50 rounded-lg text-left">
                <p className="text-slate-500 text-sm">Payment ID</p>
                <p className="text-white font-mono text-sm break-all">{paymentInfo.payment_id}</p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {paymentInfo?.payment_type === 'will_creation' && (
                <Button
                  onClick={handleGoToWill}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                  data-testid="go-to-will-btn"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Start Creating Your Will
                </Button>
              )}
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                className="w-full border-slate-700 text-white hover:bg-slate-800"
                data-testid="go-to-dashboard-btn"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard Now
              </Button>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
              <RefreshCw className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
            <p className="text-slate-400 mb-6">
              Your payment is being processed. This may take a few moments.
              You will receive a confirmation email once complete.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => window.location.reload()}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Check Status Again
              </Button>
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                className="w-full border-slate-700 text-white hover:bg-slate-800"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Failed</h1>
            <p className="text-slate-400 mb-6">
              {error || 'Something went wrong with your payment. Please try again.'}
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleGoToDashboard}
                className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
